import { createContext, useContext, useMemo } from 'react'
import { createInterviewSessionAdapter } from '@thom/folio-ui/adapters/browser/interview-session-adapter'
import { createLastProject } from '@thom/folio-ui/adapters/browser/last-project'
import { createListMemory } from '@thom/folio-ui/adapters/browser/list-memory'
import { createPageVisibilityAdapter } from '@thom/folio-ui/adapters/browser/page-visibility-adapter'
import { createThemeAdapter } from '@thom/folio-ui/adapters/browser/theme-adapter'
import { createSharesAdapter } from '@thom/folio-ui/adapters/http/shares-adapter'
import { createAuthAdapter } from '@thom/folio-ui/adapters/pocketbase/auth-adapter'
import { createClientsAdapter } from '@thom/folio-ui/adapters/pocketbase/clients-adapter'
import { createClientsLive } from '@thom/folio-ui/adapters/pocketbase/clients-live'
import { getPocketBaseClient } from '@thom/folio-ui/adapters/pocketbase/client'
import { createConnectionAdapter } from '@thom/folio-ui/adapters/pocketbase/connection-adapter'
import { createDomainsAdapter } from '@thom/folio-ui/adapters/pocketbase/domains-adapter'
import { createDomainsLive } from '@thom/folio-ui/adapters/pocketbase/domains-live'
import { createEntriesAdapter } from '@thom/folio-ui/adapters/pocketbase/entries-adapter'
import { createEntrySignal } from '@thom/folio-ui/adapters/pocketbase/entries-live'
import { createFeedbackAdapter } from '@thom/folio-ui/adapters/pocketbase/feedback-adapter'
import { createFeedbackSignals, type FeedbackSignals } from '@thom/folio-ui/adapters/pocketbase/feedback-live'
import { createInterviewsAdapter } from '@thom/folio-ui/adapters/pocketbase/interviews-adapter'
import { createInterviewSignal } from '@thom/folio-ui/adapters/pocketbase/interviews-live'
import { createIssuesAdapter } from '@thom/folio-ui/adapters/pocketbase/issues-adapter'
import { createIssueSignals } from '@thom/folio-ui/adapters/pocketbase/issues-live'
import { createKnowledgeAdapter } from '@thom/folio-ui/adapters/pocketbase/knowledge-adapter'
import { createKnowledgeLives } from '@thom/folio-ui/adapters/pocketbase/knowledge-live'
import { createPlansAdapter } from '@thom/folio-ui/adapters/pocketbase/plans-adapter'
import { createPlansLive } from '@thom/folio-ui/adapters/pocketbase/plans-live'
import { createProjectsAdapter } from '@thom/folio-ui/adapters/pocketbase/projects-adapter'
import { createProjectsLive } from '@thom/folio-ui/adapters/pocketbase/projects-live'
import { createSearchAdapter } from '@thom/folio-ui/adapters/pocketbase/search-adapter'
import { createShareLinksAdapter } from '@thom/folio-ui/adapters/pocketbase/share-links-adapter'
import type { AuthPort } from '@thom/folio-domain/core/ports/auth'
import type { Client } from '@thom/folio-domain/core/domain/client'
import type { Domain } from '@thom/folio-domain/core/domain/domain'
import type { Plan } from '@thom/folio-domain/core/domain/plan'
import type { Project } from '@thom/folio-domain/core/domain/project'
import type { ClientFilter, ClientsPort } from '@thom/folio-domain/core/ports/clients'
import type { ConnectionPort } from '@thom/folio-domain/core/ports/connection'
import type { DomainFilter, DomainsPort } from '@thom/folio-domain/core/ports/domains'
import type { EntriesPort, EntryQuery } from '@thom/folio-domain/core/ports/entries'
import type { FeedbackPort } from '@thom/folio-domain/core/ports/feedback'
import type { InterviewSessionPort } from '@thom/folio-domain/core/ports/interview-session'
import type { InterviewsPort } from '@thom/folio-domain/core/ports/interviews'
import type { IssueSignals, IssuesPort } from '@thom/folio-domain/core/ports/issues'
import type { KnowledgeLives, KnowledgePort } from '@thom/folio-domain/core/ports/knowledge'
import type { LastProjectPort } from '@thom/folio-domain/core/ports/last-project'
import type { ListMemoryPort } from '@thom/folio-domain/core/ports/list-memory'
import type { ChangeSignal, Live, Signal } from '@thom/folio-domain/core/ports/live'
import type { PageVisibilityPort } from '@thom/folio-domain/core/ports/page-visibility'
import type { PlanQuery, PlansPort } from '@thom/folio-domain/core/ports/plans'
import type { ProjectFilter, ProjectsPort } from '@thom/folio-domain/core/ports/projects'
import type { RunsPort } from '@thom/folio-domain/core/ports/runs'
import type { SearchPort } from '@thom/folio-domain/core/ports/search'
import type { ShareLinksPort } from '@thom/folio-domain/core/ports/share-links'
import type { SharesPort } from '@thom/folio-domain/core/ports/shares'
import type { ThemePort } from '@thom/folio-domain/core/ports/theme'

type Container = {
	readonly auth: AuthPort
	readonly clients: ClientsPort
	readonly connection: ConnectionPort
	readonly domains: DomainsPort
	readonly entries: EntriesPort
	readonly feedback: FeedbackPort
	readonly interviewSession: InterviewSessionPort
	readonly interviews: InterviewsPort
	readonly issues: IssuesPort
	readonly knowledge: KnowledgePort
	readonly lastProject: LastProjectPort
	readonly listMemory: ListMemoryPort
	readonly live: {
		readonly clients: Live<Client, ClientFilter>
		readonly domains: Live<Domain, DomainFilter>
		readonly plans: Live<Plan, PlanQuery>
		readonly entries: ChangeSignal<EntryQuery>
		readonly feedback: FeedbackSignals
		readonly issues: IssueSignals
		readonly interviews: Signal<string>
		readonly knowledge: KnowledgeLives
		readonly projects: Live<Project, ProjectFilter>
	}
	readonly pageVisibility: PageVisibilityPort
	readonly plans: PlansPort
	readonly projects: ProjectsPort
	readonly runs?: RunsPort
	readonly search: SearchPort
	readonly shareLinks: ShareLinksPort
	readonly shares: SharesPort
	readonly theme: ThemePort
}

const createContainer = (): Container => {
	const connection = createConnectionAdapter()
	const realtime = { client: getPocketBaseClient(), connection }

	return {
		auth: createAuthAdapter(),
		clients: createClientsAdapter(),
		connection,
		domains: createDomainsAdapter(),
		entries: createEntriesAdapter(),
		feedback: createFeedbackAdapter(),
		interviewSession: createInterviewSessionAdapter(),
		interviews: createInterviewsAdapter(),
		issues: createIssuesAdapter(),
		knowledge: createKnowledgeAdapter(),
		lastProject: createLastProject(),
		listMemory: createListMemory(),
		live: {
			clients: createClientsLive(realtime),
			domains: createDomainsLive(realtime),
			entries: createEntrySignal(realtime),
			feedback: createFeedbackSignals(realtime),
			issues: createIssueSignals(realtime),
			interviews: createInterviewSignal(realtime),
			knowledge: createKnowledgeLives(realtime),
			plans: createPlansLive(realtime),
			projects: createProjectsLive(realtime),
		},
		pageVisibility: createPageVisibilityAdapter(),
		plans: createPlansAdapter(),
		projects: createProjectsAdapter(),
		search: createSearchAdapter(),
		shareLinks: createShareLinksAdapter(),
		shares: createSharesAdapter(),
		theme: createThemeAdapter(),
	}
}

const ContainerContext = createContext<Container | undefined>(undefined)

type ProviderProps = {
	readonly container?: Container
	readonly children: React.ReactNode
}

const ContainerProvider = ({ container, children }: ProviderProps) => {
	const value = useMemo(() => container ?? createContainer(), [container])

	return <ContainerContext.Provider value={value}>{children}</ContainerContext.Provider>
}

const useContainer = (): Container => {
	const container = useContext(ContainerContext)

	if (!container) {
		throw new Error('useContainer must be used inside a ContainerProvider')
	}

	return container
}

export { ContainerProvider, createContainer, useContainer }
export type { Container }
